import { IntegrationType } from '@principle-theorem/integrations';
import {
  CommunicationDirection,
  type IOrganisation,
  SMSDeliveryStatus,
  type SMSMessage,
} from '@principle-theorem/principle-core/interfaces';
import { initMoment, toMoment, type WithRef } from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { isViaApiSMSProvider } from '../organisation/organisation';

const unsentMessageFailsAfter = moment.duration(30, 'seconds');

const allowedStatusChanges: Record<SMSDeliveryStatus, SMSDeliveryStatus[]> = {
  [SMSDeliveryStatus.Pending]: [
    SMSDeliveryStatus.Sent,
    SMSDeliveryStatus.Delivered,
    SMSDeliveryStatus.Failed,
  ],
  [SMSDeliveryStatus.Sent]: [
    SMSDeliveryStatus.Delivered,
    SMSDeliveryStatus.Failed,
  ],
  [SMSDeliveryStatus.Delivered]: [],
  [SMSDeliveryStatus.Failed]: [],
};

/**
 * The 30 second timeout only suits built-in providers, which send straight
 * away. A practice's own gateway sends on its own schedule, so its messages
 * only fail when the gateway reports it.
 */
export class SMSMessageDelivery {
  static hasFailed(
    message: WithRef<SMSMessage>,
    organisation: IOrganisation,
    at: moment.Moment = initMoment()
  ): boolean {
    const provider =
      'metadata' in message
        ? message.metadata.provider
        : organisation.integrations?.smsProvider;
    if (provider === IntegrationType.ViaApi) {
      return message.status === SMSDeliveryStatus.Failed;
    }
    const failsAt = toMoment(message.createdAt).add(unsentMessageFailsAfter);
    return message.status === SMSDeliveryStatus.Pending && at.isAfter(failsAt);
  }

  /**
   * The gateway is notified before the provider tag is written, so its report
   * can arrive while the pending message is still untagged.
   */
  static canGatewayReport(
    message: SMSMessage,
    organisation: IOrganisation
  ): boolean {
    if (message.direction !== CommunicationDirection.Outbound) {
      return false;
    }
    if ('metadata' in message) {
      return message.metadata.provider === IntegrationType.ViaApi;
    }
    return (
      message.status === SMSDeliveryStatus.Pending &&
      isViaApiSMSProvider(organisation)
    );
  }

  /**
   * A status only moves forward. Reporting the current status again is
   * allowed so a gateway can safely retry.
   */
  static canChangeStatus(
    from: SMSDeliveryStatus,
    to: SMSDeliveryStatus
  ): boolean {
    return from === to || allowedStatusChanges[from].includes(to);
  }
}
